import { SETTINGS } from '../SETTINGS';
import * as THREE from 'three';
import vertexShader from '../shaders/_particle.vert';
import fragmentShader from '../shaders/_particle.frag';

export class Particles {
  private particles: THREE.Points;
  private material: THREE.ShaderMaterial;
  private geometry: THREE.BufferGeometry;

  public constructor(renderer: THREE.WebGLRenderer) {
    this.geometry = this.createGeometry();
    this.material = this.createMaterial(renderer);
    this.particles = new THREE.Points(this.geometry, this.material);
  }

  public getInstance() {
    return this.particles;
  }

  public getMaterial() {
    return this.material;
  }

  /**
   * パーティクルのジオメトリを作成
   * @returns ジオメトリ
   */
  private createGeometry() {
    const particleCount = SETTINGS.PARTICLE.PARTICLE_COUNT;
    const geometry = new THREE.BufferGeometry();

    const positions = new Float32Array(particleCount * 3);
    const velocities = new Float32Array(particleCount * 3);
    const lifetimes = new Float32Array(particleCount);
    const startTimes = new Float32Array(particleCount);
    const colors = new Float32Array(particleCount * 3);
    const randoms = new Float32Array(particleCount);

    // ランダムなカラーパレット
    const colorPalette = this.createColorPalette();

    // パーティクルのジオメトリを初期化
    for (let i = 0; i < particleCount; i++) {
      this.initParticleGeometry(
        i,
        positions,
        velocities,
        lifetimes,
        startTimes,
        randoms,
        colors,
        colorPalette
      );
    }

    // ジオメトリに属性を設定
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aVelocity', new THREE.BufferAttribute(velocities, 3));
    geometry.setAttribute('aLifetime', new THREE.BufferAttribute(lifetimes, 1));
    geometry.setAttribute('aStartTime', new THREE.BufferAttribute(startTimes, 1));
    geometry.setAttribute('aColor', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('aRandom', new THREE.BufferAttribute(randoms, 1));

    return geometry;
  }


  // Helper ------------------------------------------------------------
  /**
   * パーティクルのマテリアルを作成
   * for constructor
   * @param renderer
   * @returns マテリアル
   */
  private createMaterial(renderer: THREE.WebGLRenderer) {
    const material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0.0 },
        uSize: { value: 30.0 * renderer.getPixelRatio() },
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: true,
      blending: THREE.NormalBlending,
      depthWrite: false,
    });
    return material;
  }
  /**
   * パーティクルのジオメトリを初期化
   * for createGeometry()
   * @param index
   * @param positions
   * @param velocities
   * @param lifetimes
   * @param startTimes
   * @param randoms
   * @param colors
   * @param colorPalette
   */
  private initParticleGeometry(
    index: number,
    positions: Float32Array,
    velocities: Float32Array,
    lifetimes: Float32Array,
    startTimes: Float32Array,
    randoms: Float32Array,
    colors: Float32Array,
    colorPalette: THREE.Color[]
  ) {
    // 位置
    positions[index * 3 + 0] = (Math.random() - 0.5) * SETTINGS.PARTICLE.POSITION.X;
    positions[index * 3 + 1] = 0.0;
    positions[index * 3 + 2] = 0.0;

    // 速度
    velocities[index * 3 + 0] = (Math.random() - 0.5) * SETTINGS.PARTICLE.VELOCITY.X;
    velocities[index * 3 + 1] = 2.0 + Math.random() * SETTINGS.PARTICLE.VELOCITY.Y;
    velocities[index * 3 + 2] = 0.0;

    // 寿命
    lifetimes[index] = 2.5 + Math.random() * 2.0;
    // 開始時間
    startTimes[index] = Math.random();
    // ランダム
    randoms[index] = 0.5 + 2.0 * Math.random();

    // カラー
    const randomColor = colorPalette[Math.floor(Math.random() * colorPalette.length)];
    colors[index * 3 + 0] = randomColor.r;
    colors[index * 3 + 1] = randomColor.g;
    colors[index * 3 + 2] = randomColor.b;
  }
  /**
   * パーティクルのカラーパレットを作成
   * for createGeometry()
   * @returns カラーパレット
   */
  private createColorPalette(): THREE.Color[] {
    const colorPalette = [
      new THREE.Color('#ff6b35'), // オレンジ
      new THREE.Color('#f7931e'), // 明るいオレンジ
      new THREE.Color('#ffd23f'), // 黄色
      new THREE.Color('#ff4757'), // 赤
      new THREE.Color('#ff3838'), // 明るい赤
      new THREE.Color('#ff9ff3'), // ピンク
      new THREE.Color('#54a0ff'), // 青
      new THREE.Color('#5f27cd'), // 紫
      new THREE.Color('#00d2d3'), // シアン
      new THREE.Color('#ff9f43'), // 金色
    ];
    return colorPalette;
  }
}
